import { db } from './db.ts';

/**
 * Pemantau kuota penyimpanan (T-P5-17, FR-SYNC-13).
 *
 * Kuota IndexedDB habis di tengah kunjungan berarti foto atau catatan gagal
 * tersimpan tanpa cara memulihkannya. Karena itu ada dua ambang: peringatan
 * saat sisa < 100 MB, dan blokir penarikan data baru saat < 20 MB — antrean
 * kirim tetap boleh jalan, karena justru itu yang mengosongkan ruang.
 */

export const WARN_THRESHOLD_BYTES = 100 * 1024 * 1024;
export const BLOCK_THRESHOLD_BYTES = 20 * 1024 * 1024;

export interface StorageStatus {
  supported: boolean;
  usageBytes: number;
  quotaBytes: number;
  availableBytes: number;
  level: 'ok' | 'warning' | 'critical';
  persisted: boolean;
  pendingMediaBytes: number;
}

export async function storageStatus(): Promise<StorageStatus> {
  const pendingMediaBytes = await pendingBytes();

  if (!navigator.storage?.estimate) {
    // Peramban tanpa Storage API: tidak ada dasar untuk memblokir apa pun.
    return {
      supported: false,
      usageBytes: 0,
      quotaBytes: 0,
      availableBytes: Number.POSITIVE_INFINITY,
      level: 'ok',
      persisted: false,
      pendingMediaBytes,
    };
  }

  const estimate = await navigator.storage.estimate();
  const usageBytes = estimate.usage ?? 0;
  const quotaBytes = estimate.quota ?? 0;
  const availableBytes = Math.max(quotaBytes - usageBytes, 0);

  return {
    supported: true,
    usageBytes,
    quotaBytes,
    availableBytes,
    level:
      availableBytes < BLOCK_THRESHOLD_BYTES
        ? 'critical'
        : availableBytes < WARN_THRESHOLD_BYTES
          ? 'warning'
          : 'ok',
    persisted: (await navigator.storage.persisted?.()) ?? false,
    pendingMediaBytes,
  };
}

async function pendingBytes(): Promise<number> {
  const items = await db.mediaQueue.where('status').anyOf('PENDING', 'UPLOADING').toArray();
  return items.reduce((total, item) => total + item.sizeBytes, 0);
}

export class StorageFullError extends Error {
  constructor(readonly status: StorageStatus) {
    super(
      `Penyimpanan perangkat hampir penuh (sisa ${Math.round(status.availableBytes / 1024 / 1024)} MB). Sinkronkan atau kosongkan ruang sebelum menarik data baru.`,
    );
    this.name = 'StorageFullError';
  }
}

/** Dipanggil sebelum `sync/pull`: menambah cache saat ruang kritis akan merusaknya. */
export async function assertStorageAvailable(): Promise<StorageStatus> {
  const status = await storageStatus();
  if (status.level === 'critical') throw new StorageFullError(status);
  return status;
}

/**
 * Minta penyimpanan persisten sekali saat perangkat disiapkan.
 *
 * Tanpa ini peramban boleh membuang IndexedDB saat ruang menipis — dan yang
 * hilang adalah kunjungan yang belum terkirim.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted?.()) return true;
  return navigator.storage.persist();
}
